import {Component, type ReactNode} from 'react';
import styles from './style.module.css';
import {appsignal} from '@/appsignal.client';
import {type SpanData} from '@appsignal/javascript/dist/cjs/span';
import {logger} from '@/lib/logger';
import {IS_PRODUCTION} from '@/util/constants';

const log = logger.tag('ErrorBoundary');

interface Props extends Pick<SpanData, 'action' | 'tags'> {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    error?: Error;
    hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {hasError: false};
    }

    static getDerivedStateFromError(error: Error): State {
        return {hasError: true, error};
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        log.error('ErrorBoundary caught an error:', error, {
            componentStack: errorInfo?.componentStack,
        });
        if (IS_PRODUCTION) {
            appsignal.sendError(error, (span) => {
                if (this.props.tags) span.setTags(this.props.tags);
                if (this.props.action) span.setAction(this.props.action);
                span.setTags({
                    react_component_stack: (
                        errorInfo?.componentStack ?? ''
                    ).slice(0, 1000),
                });
            });
        }
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) return this.props.fallback;
            return (
                <div className={styles.errorContainer}>
                    <div className={styles.errorContent}>
                        <h2 className={styles.errorTitle}>
                            Something went wrong
                        </h2>
                        <p className={styles.errorMessage}>
                            {this.state.error?.message ??
                                'An unexpected error occurred'}
                        </p>
                        <button
                            className={styles.errorButton}
                            onClick={() => this.setState({hasError: false})}
                        >
                            Try again
                        </button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}
